import { FastlyImage, LazyAPIImage } from '@components/common/image'
import Button from '@components/atoms/button'
import Link from 'next/link'
import { Play, FileText, DollarSign, BookOpen, Users } from 'react-feather'

type CourseTag = {
  id: number
  title: string
  slug: string
}

export type CourseSubject = 'Coding' | 'Math' | 'ELA'

export type Course = {
  id: number
  title: string
  description: string | null
  subject?: CourseSubject
  thumbnailLandscape: string | null
  ageRange: string | null
  gradeRange: string | null
  estimatedCompletionTime: string | null
  studentsEnrolledCount: number
  totalLessonsCount: number | string
  tags: CourseTag[] | null
  landingPageUrl: string | null
  paymentPageUrl: string | null
  curriculumPdf: string | null
  introVideoUrl: string | null
  trialCourseSlug: string | null
  pillContent?: {
    text: string
    color: string
  }
}

export const isAICourse = (course: Course): boolean =>
  course.title.toLowerCase().split(' ').includes('ai')

type CourseCardProps = {
  course: Course
  onVideoPlay?: (course: Course) => void
  onViewPricing?: () => void
  thumbnailDimensions?: {
    width: number
    height: number
  }
  isCampCourse?: boolean
  titleAs?: 'h2' | 'h3'
}

const CourseCard = ({
  course,
  onVideoPlay,
  onViewPricing,
  thumbnailDimensions,
  isCampCourse = false,
  titleAs: TitleTag = 'h2',
}: CourseCardProps) => {
  const handleViewPricing = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    if (onViewPricing) {
      e.preventDefault()
      onViewPricing()
    }
  }

  const isAI = isAICourse(course)

  // Get primary tag for badge display
  const primaryTag =
    course.tags && course.tags.length > 0 ? course.tags[0] : null

  // Check if right sidebar has any content to show
  const hasRightSidebarContent =
    course.ageRange ||
    course.gradeRange ||
    (typeof course.totalLessonsCount === 'number' &&
      course.totalLessonsCount > 0) ||
    typeof course.totalLessonsCount === 'string' ||
    course.estimatedCompletionTime

  const handleCardClick = () => {
    if (course.trialCourseSlug) {
      window.open(
        `/trial-class/booking/?course=${course.trialCourseSlug}`,
        '_blank',
        'noopener,noreferrer'
      )
    } else if (isCampCourse && course.landingPageUrl) {
      window.location.href = course.landingPageUrl
    }
  }

  const isCardClickable =
    !!course.trialCourseSlug || (isCampCourse && !!course.landingPageUrl)

  return (
    <div
      className={`relative bg-white rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 mx-auto text-grey text-left${
        isCardClickable ? ' cursor-pointer' : ''
      }`}
      style={{ border: '1px solid #d5d5d5' }}
      onClick={handleCardClick}
    >
      {/* AI Tag Badge - positioned on card */}
      {isAI && (
        <div className="absolute top-0 left-0 z-10 ml-1">
          <FastlyImage
            src="/images/ai-tag.png"
            alt="AI"
            width={30}
            height={40}
          />
        </div>
      )}

      <div className="flex flex-col md:flex-row p-2">
        {/* Left: Video Thumbnail */}
        <div
          className={`relative ${
            thumbnailDimensions ? 'md:w-2/5 lg:w-auto' : 'md:w-2/5 lg:w-1/3'
          } flex-shrink-0`}
        >
          {/* Tag Badge - only show if not AI course */}
          {!isAI && primaryTag && (
            <div className="absolute top-0 left-0 z-10">
              <div className="bg-orange text-white font-600 text-base px-3 py-1 rounded-br-lg">
                {primaryTag.title}
              </div>
            </div>
          )}

          {/* Video Thumbnail */}
          <div className="relative h-full min-h-[200px] bg-grey-100 rounded-lg overflow-hidden">
            {course.thumbnailLandscape ? (
              course.landingPageUrl ? (
                <a
                  href={course.landingPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <LazyAPIImage
                    src={course.thumbnailLandscape}
                    alt={`Image for ${course.title} course`}
                    className="w-full h-full object-cover"
                    width={thumbnailDimensions?.width || 400}
                    height={thumbnailDimensions?.height || 300}
                  />
                </a>
              ) : (
                <LazyAPIImage
                  src={course.thumbnailLandscape}
                  alt={`Image for ${course.title} course`}
                  className="w-full h-full object-cover"
                  width={thumbnailDimensions?.width || 400}
                  height={thumbnailDimensions?.height || 300}
                />
              )
            ) : (
              <div className="w-full h-full bg-grey-200 flex items-center justify-center">
                <BookOpen className="text-grey-400 w-16 h-16" />
              </div>
            )}

            {/* Play Button Overlay */}
            {course.introVideoUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onVideoPlay?.(course)
                }}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 focus:outline-none"
                aria-label="Play video"
              >
                <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center hover:bg-orange transition-colors group">
                  <Play
                    className="text-grey-900 group-hover:text-white transition-colors ml-1"
                    size={24}
                    fill="currentColor"
                  />
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Middle: Main Content */}
        <div className="flex-1 p-1 pt-4 md:p-4 md:ml-2 flex flex-col">
          <div className="flex-grow">
            {course.pillContent && (
              <div
                className={`inline-block px-3 py-1 text-sm font-600 rounded-full mb-3 border ${course.pillContent.color}`}
              >
                {course.pillContent.text}
              </div>
            )}
            {course.landingPageUrl ? (
              <a
                href={course.landingPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer group"
                onClick={(e) => e.stopPropagation()}
              >
                <TitleTag className="text-2xl font-600 group-hover:text-orange mb-3 leading-tight transition-colors">
                  {course.title}
                </TitleTag>
              </a>
            ) : (
              <TitleTag className="text-2xl font-600 mb-3 leading-tight">
                {course.title}
              </TitleTag>
            )}

            {course.description && (
              <p className="mb-4 leading-tight text-sm">{course.description}</p>
            )}

            {/* Course Info - Mobile Only (below description) */}
            {hasRightSidebarContent && (
              <div
                className="md:hidden grid grid-cols-2 gap-2 mb-4 p-3 rounded-lg"
                style={{ backgroundColor: '#F9FAFB' }}
              >
                {course.ageRange && (
                  <div className="flex items-center">
                    <span className="mr-2">🎓</span>
                    <p>{course.ageRange}</p>
                  </div>
                )}

                {course.gradeRange && (
                  <div className="flex items-center">
                    <span className="mr-2">🎒</span>
                    <p>{course.gradeRange}</p>
                  </div>
                )}

                {typeof course.totalLessonsCount === 'number' &&
                  course.totalLessonsCount > 0 && (
                    <div className="flex items-center">
                      <span className="mr-2">📖</span>
                      <p>{course.totalLessonsCount} Lessons</p>
                    </div>
                  )}

                {typeof course.totalLessonsCount === 'string' && (
                  <div className="flex items-center">
                    <span className="mr-2">📖</span>
                    <p>{course.totalLessonsCount}</p>
                  </div>
                )}

                {course.estimatedCompletionTime && (
                  <div className="flex items-center">
                    <span className="mr-2">🕒</span>
                    <p>{course.estimatedCompletionTime}</p>
                  </div>
                )}
              </div>
            )}

            {/* Action Links */}
            {!isCampCourse && (
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                {course.curriculumPdf && (
                  <a
                    href={course.curriculumPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center hover:underline transition-colors duration-200"
                    style={{ color: '#155DFC' }}
                  >
                    <FileText size={15} className="mr-1" />
                    <span>View curriculum</span>
                  </a>
                )}
                <a
                  href={course.landingPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleViewPricing}
                  className="flex items-center hover:underline transition-colors duration-200"
                  style={{ color: '#155DFC' }}
                >
                  <DollarSign size={15} />
                  <span>View pricing</span>
                </a>
              </div>
            )}
          </div>

          {/* Students Enrolled */}
          {course.studentsEnrolledCount > 0 && (
            <div className="flex items-center mb-2 text-sm">
              <Users size={20} className="mr-2 text-grey-700" />
              <span>
                <span className="font-600">
                  {course.studentsEnrolledCount}+
                </span>{' '}
                <span className="text-grey-700 font-500">
                  Students enrolled
                </span>
              </span>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3">
            {!isCampCourse && course.trialCourseSlug && (
              <Link
                href={`/trial-class/booking/?course=${course.trialCourseSlug}`}
                prefetch={false}
              >
                <a
                  onClick={(e) => e.stopPropagation()}
                  className="w-full lg:w-auto"
                >
                  <Button
                    variant="outline"
                    intent="primary"
                    size="md"
                    className="border-orange text-orange hover:bg-orange hover:text-white w-full md:w-auto"
                  >
                    Try a free lesson
                  </Button>
                </a>
              </Link>
            )}
            {course.paymentPageUrl && (
              <a
                href={course.paymentPageUrl}
                target={isCampCourse ? '_self' : '_blank'}
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="w-full lg:w-auto"
              >
                <Button
                  variant="solid"
                  intent="primary"
                  size="md"
                  className="bg-orange text-white hover:bg-opacity-90 w-full md:w-auto"
                >
                  {isCampCourse ? 'Claim your spot' : 'Enroll now'}
                </Button>
              </a>
            )}
            {course.landingPageUrl && (
              <a
                href={course.landingPageUrl}
                target={isCampCourse ? '_self' : '_blank'}
                rel="noopener noreferrer"
                className="text-orange font-600 hover:underline flex items-center w-full md:w-auto justify-center md:justify-start"
                onClick={(e) => e.stopPropagation()}
              >
                {isCampCourse ? 'View camp details' : 'View course details'}
              </a>
            )}
          </div>
        </div>

        {/* Right: Course Info - Desktop Only */}
        {hasRightSidebarContent && (
          <div className="hidden md:flex md:w-40 bg-white p-4 md:flex-col md:space-y-2 border-l border-grey-200 text-sm text-grey-900">
            {course.ageRange && (
              <div className="flex items-center">
                <span className="mr-2">🎓</span>
                <p>{course.ageRange}</p>
              </div>
            )}

            {course.gradeRange && (
              <div className="flex items-center">
                <span className="mr-2">🎒</span>
                <p>{course.gradeRange}</p>
              </div>
            )}

            {typeof course.totalLessonsCount === 'number' &&
              course.totalLessonsCount > 0 && (
                <div className="flex items-center">
                  <span className="mr-2">📖</span>
                  <p>{course.totalLessonsCount} Lessons</p>
                </div>
              )}

            {typeof course.totalLessonsCount === 'string' && (
              <div className="flex items-center">
                <span className="mr-2">📖</span>
                <p>{course.totalLessonsCount}</p>
              </div>
            )}

            {course.estimatedCompletionTime && (
              <div className="flex items-center">
                <span className="mr-2">🕒</span>
                <p>{course.estimatedCompletionTime}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CourseCard
